import { FunctionComponent, useCallback, FormEvent, useEffect, useState, useRef } from "react";
import styled from "styled-components";
import {
    AtSign,
    Briefcase,
    Building2,
    Cake,
    Calendar,
    ChevronDown,
    EyeOff,
    Languages,
    Mail,
    Phone,
    RefreshCw,
    Sparkles,
    StickyNote,
    Tag,
    Tags,
    UserPlus,
    Users,
} from "lucide-react";
import Text from "../../components/Text";
import Select from '../../components/select/Select';
import FormControl from '../../components/FormControl'
import { useConnectionsHandlers } from "../../services/actions/connections/useConnectionsHandlers";
import DateComponent from "../../components/DateComponent";
import { selectConnectionsFilter, useConnectionsStore } from "../../stores/connections/useConnectionsStore";
import Column from '../../components/Column'
import AdvancedFilterFragment from "../AdvancedFilterFragment";
import { ITag, IUser } from "../../interfaces/models/connection";
import { industryOptions } from '../../assets/utils/industryOptions'
import { useAppHandlers } from "../../services/actions/app/useAppHandlers";
import { SidebarTypes } from "../../interfaces/store/appStore";
import Row from "../../components/Row";
import { useNotesHandlers } from "../../services/actions/notes/useNotesHandlers";
import ProfilePhoto from "../../components/ProfilePhoto";
import MinMaxRangeDropdown from "../dropdowns/MinMaxRangeDropdown";
import { useAuthStore } from "../../stores/auth/useAuthStore";
import AppTag from "../AppTag";
import AdvancedSearchDropdown from "../AdvancedSearchDropdown";
import LastMessagedFilterFragment from "../LastMessagedFilterFragment";
import AutotagsFilterFragment from "../AutotagsFilterFragment";
import JobCompanyFilterFragment from "../JobCompanyFilterFragment";
import { styles } from "../../styles/themes/style";
import { getFilterSectionsState, setFilterSectionsState, FilterSectionKey } from "./filterSectionsStorage";

const filterIcon = (Icon: typeof Users) => <Icon size={16} className="filter-field-icon" />

interface IFilterConnectionsFormProps { }

const FilterConnectionsForm: FunctionComponent<IFilterConnectionsFormProps> = () => {
    const textRef = useRef<HTMLDivElement>(null)

    const { setScopedConnectionsFilterHandler: setConnectionsFilterHandler, setClearScopedConnectionsFilterHandler: setClearConnectionsFilterHandler } = useConnectionsHandlers();
    const { store } = useConnectionsStore();
    const connectionsFilter = selectConnectionsFilter(store);
    const { store: { workspace} } = useAuthStore()
    const {
        connectedAtFrom, 
        connectedAtTo, 
        lastMessagedFrom, 
        lastMessagedTo,
        conversationStatus,
        conversationOwners,
        onlyLast,
        birthDateFrom, 
        birthDateTo, 
        selectedTags, 
        selectedExcludeTags, 
        tagCondition, 
        selectedLanguages, 
        languageCondition, 
        usersMutualCondition, 
        selectedUsersMutual,
        searchByFollowers,
        searchByMaxFollowers,
        companyEmployeesMin,
        companyEmployeesMax,
        autotagsFilter,
        hasJobChanges,
        hasCompanyChanges
    } = connectionsFilter
    const { tagFilterOptions, languageFilterOptions, excludeTagFilterOptions } = store.connectionFilterOptions
    const { workspaceUsersPublicAllHandler } = useNotesHandlers();
    const [workspaceUsers, setWorkspaceUsers] = useState<any>([])
    const [openSections, setOpenSections] = useState(getFilterSectionsState)

    const { openSidebar, closeSidebar } = useAppHandlers()

    const toggleSection = (section: FilterSectionKey) => {
        setOpenSections((prev: typeof openSections) => {
            const next = { ...prev, [section]: !prev[section] }
            setFilterSectionsState(next)
            return next
        })
    }

    const inputTitleLanguages = "Languages";
    const inputTitleTags = "Tags";
    const inputTitleExcludeTags = "Exclude Tags";
    const inputTitleFollowers = "Number of Followers";
    const inputTitleCompanyEmployees = "Company headcount";
    const inputTitleAutotags = "Autotags";
    const infoTitleLanguages = "Choose whether you want to get items that have all the selected languages (AND) or any of the selected languages (OR).";
    const infoTitleTags = "Choose whether you want to get items that have all the selected tags (AND) or any of the selected tags (OR).";
    const inputTitleMutualConnections = "Connected members";

    const hiddenConnectionsOptions = [
        { value: 'no', label: 'No' },
        { value: 'yes', label: 'Yes' },
        { value: 'all', label: 'All' }
    ];

    const contactInfoOptions = [
        { value: 'all', label: 'All' },
        { value: 'yes', label: 'With Li Email' },
        { value: 'no', label: 'Without Li Email' },  
    ];

    const notesOptions = [
        { value: 'no', label: 'No' },
        { value: 'yes', label: 'Yes' },
        { value: 'all', label: 'All' }
    ];

    const privateEmailOptions = [
        { value: 'all', label: 'All' },
        { value: 'yes', label: 'With Personal Email' },
        { value: 'no', label: 'Without Personal Email' },  
    ];

    const businessEmailOptions = [
        { value: 'all', label: 'All' },
        { value: 'yes', label: 'With Business Email' },
        { value: 'no', label: 'Without Business Email' },  
    ];

    const phoneOptions = [
        { value: 'all', label: 'All' },
        { value: 'yes', label: 'With Phone' },
        { value: 'no', label: 'Without Phone' },  
    ];

    const liPhoneOptions = [
        { value: 'all', label: 'All' },
        { value: 'yes', label: 'With Li Phone' },
        { value: 'no', label: 'Without Li Phone' },  
    ];

    // a select counts as filtering only when it holds something other than its neutral option
    const isSelectValueActive = (value: any, neutralValue: string = 'all') => {
        const resolvedValue = typeof value === 'string' ? value : value?.value
        return !!resolvedValue && resolvedValue !== neutralValue
    }

    useEffect(() => {
        workspaceUsersHandler()
    }, [])

    const updateForm = useCallback((control: any, newValue: string) => {
        const updatedFilter = {
            ...connectionsFilter,
            [control]: newValue,
            sortClicked: false
        };

        setConnectionsFilterHandler(updatedFilter);
    }, [connectionsFilter, setConnectionsFilterHandler]);

    const updateFormLastMessaged = useCallback((control: any, newValue: any) => {
        let updatedFilter;
        if (Array.isArray(control) && control.length === 2) {
            updatedFilter = {
                ...connectionsFilter,
                [control[0]]: Array.isArray(newValue) && newValue.length === 2 ? newValue[0] : newValue,
                [control[1]]: Array.isArray(newValue) && newValue.length === 2 ? newValue[1] : newValue
            };
        } else {
            updatedFilter = {
                ...connectionsFilter,
                [control]: newValue,
                sortClicked: false
            };
        }

        setConnectionsFilterHandler(updatedFilter);
    }, [connectionsFilter, setConnectionsFilterHandler]);

    const updateMinMax = useCallback((controls: { [key: string]: number }[]) => {
        const updatedFilter = {
            ...connectionsFilter,
            ...controls.reduce((buildingValue, currentValue) => ({...buildingValue, ...currentValue}), {}),
            sortClicked: false
        };

        setConnectionsFilterHandler(updatedFilter);
    }, [connectionsFilter, setConnectionsFilterHandler]);

    const resetDateFilter = (controlFrom: any, controlTo: any) => {
        const updatedFilter = {
           ...connectionsFilter,
           [controlFrom]: undefined,
           [controlTo]: undefined,
           sortClicked: false
       }; 
       setConnectionsFilterHandler(updatedFilter);
   };

   const resetLastMsgdFilter = (controlFrom: any, controlTo: any, controlStatus?: any, controlOwner?: any, controlOnlyLast?: any) => {
        const updatedFilter = {
            ...connectionsFilter,
            [controlFrom]: undefined,
            [controlTo]: undefined,
            [controlStatus]: undefined,
            [controlOwner]: undefined,
            [controlOnlyLast]: undefined,
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

    const saveSavedFiltersHandler = useCallback(async (e: FormEvent) => {
        e.preventDefault()
        openSidebar(SidebarTypes.SAVE_FILTER)
    }, [connectionsFilter]);

    const showResults = useCallback(async (e: FormEvent) => {
        e.preventDefault()
        closeSidebar(SidebarTypes.CONNECTION_FILTER)
    }, [connectionsFilter]);

    const resetFormHandler = useCallback(() => {
        //resetConnectionsFilterHandler(updatedFilter); 
        //this action should be in the handlers and as a store action since initial state of the filters, not searches is saved there as a constant
        //and it should also be accessible in the store from other parts of the app

        setClearConnectionsFilterHandler();
    }, [connectionsFilter, setConnectionsFilterHandler]);

    // job/company changes filter
    const setHasJobChanges = (value: boolean) => {
        const updatedFilter = {
            ...connectionsFilter,
            hasJobChanges: value ? 'yes' : undefined,
            sortClicked: false
        };
        setConnectionsFilterHandler(updatedFilter);
    };

    const setHasCompanyChanges = (value: boolean) => {
        const updatedFilter = {
            ...connectionsFilter,
            hasCompanyChanges: value ? 'yes' : undefined,
            sortClicked: false
        };
        setConnectionsFilterHandler(updatedFilter);
    };

    // tags filter
    const setSelectedTags = (newlySelectedTags: ITag[]) => {
        let noTags = false
        newlySelectedTags = newlySelectedTags.filter((selectedTag => {
            if(selectedTag._id === 'noTags') noTags = true
            return selectedTag._id !== 'noTags'
        }))
        const updatedFilter = {
            ...connectionsFilter,
            selectedTags: [...newlySelectedTags],
            noTags,
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

     const resetSelectedTags = () => {
        //setNoTagsSelected(false);
        const updatedFilter = {
            ...connectionsFilter,
            selectedTags: [],
            noTags: false,
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

    const setSelectedExcludeTags = (newlySelectedTags: ITag[]) => {
            const updatedFilter = {
                ...connectionsFilter,
                selectedExcludeTags: [...newlySelectedTags],
                sortClicked: false
            }; 
            setConnectionsFilterHandler(updatedFilter);
    };

    const removeSelectedExcludeTags = (removedTags: ITag[]) => {
        const tagsArray = connectionsFilter.selectedExcludeTags
        const index = tagsArray.findIndex((tag: any) => tag._id === removedTags?.[0]._id)
        if (index !== -1) {
            tagsArray.splice(index, 1);
            const updatedFilter = {
                ...connectionsFilter,
                selectedExcludeTags: [...tagsArray],
                sortClicked: false
            };
            setConnectionsFilterHandler(updatedFilter);
        }
    };

    const resetSelectedExcludeTags = () => {
        const updatedFilter = {
            ...connectionsFilter,
            selectedExcludeTags: [],
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

    // languages filter 
    const setSelectedLanguages = (newlySelectedLanguages: string[]) => {
        const updatedFilter = {
            ...connectionsFilter,
            selectedLanguages: [...newlySelectedLanguages],
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

     const resetSelectedLanguages = () => {
        //setNoTagsSelected(false);
        const updatedFilter = {
            ...connectionsFilter,
            selectedLanguages: [],
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

    const setSelectedUsersMutual = (newlySelectedUsers: IUser[]) => {
        const updatedFilter = {
            ...connectionsFilter,
            selectedUsersMutual: [...newlySelectedUsers],
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

    const resetSelectedUsersMutual = () => {
        const updatedFilter = {
            ...connectionsFilter,
            selectedUsersMutual: [],
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

    const updateFormAutotags = useCallback((control: any, newValue: any) => {
        const updatedFilter = {
                ...connectionsFilter,
                [control]: newValue,
                sortClicked: false
            };
        setConnectionsFilterHandler(updatedFilter);
    }, [connectionsFilter, setConnectionsFilterHandler]);

    const resetSelectedAutotags = () => {
        const updatedFilter = {
            ...connectionsFilter,
            autotagsFilter: undefined,
            sortClicked: false
        }; 
        setConnectionsFilterHandler(updatedFilter);
    };

    const workspaceUsersHandler = async () => {
        const result = await workspaceUsersPublicAllHandler({
            condition: 'or',
            name: '',
            email: '',
            getAll: 'yes' 
        })
        setWorkspaceUsers(result)
    }

    return (
        <StyledForm>
            <FilterHeader>
                <FilterTitle>Filter</FilterTitle>
            </FilterHeader>
            <FiltersBody>
                <FilterSection>
                    <SectionToggle type="button" onClick={() => toggleSection('actions')}>
                        <ChevronDown size={14} style={{ transform: openSections.actions ? undefined : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                        <SectionTitle>Actions</SectionTitle>
                    </SectionToggle>
                    {openSections.actions && (
                        <FilterGrid>
                            <AdvancedFilterFragment
                                icon={filterIcon(Users)}
                                styledRow={true}
                                filterOptions={workspaceUsers.map((user: IUser) => ({
                                    value: `${user.firstName} ${user.lastName}`,
                                    label: <StyledContainer>
                                            <ProfilePhoto
                                                tooltipMessage={`${user?.firstName} ${user?.lastName}`}
                                                capitalLetters={`${user?.firstName?.charAt(0).toUpperCase()}${user?.lastName?.charAt(0).toUpperCase()}`} />
                                            <MemberInfo>
                                                <Text $ellipsis="0">{user?.firstName + ' ' + user?.lastName}</Text>
                                                <Text $paragraph3 $placeholder ref={textRef} $ellipsis="0">{user?.email}</Text>
                                            </MemberInfo>
                                    </StyledContainer>,
                                    id: user._id,
                                    selected: false,
                                    object: user
                                }))}
                                upgradePlan={workspace?.isStarter}
                                selectedItems={selectedUsersMutual}
                                infoTitle={infoTitleTags}
                                inputTitle={inputTitleMutualConnections}
                                tabs={[{title: 'All', slug: 'Or', selected: usersMutualCondition === 'Or'}, {title: 'Shared', slug: 'And', selected: usersMutualCondition === 'And'}, {title: 'Unique', slug: 'Xor', selected: usersMutualCondition === 'Xor'}]}
                                onSelectedTabHandler={(tab: string) => updateForm('usersMutualCondition', tab)} 
                                onSelectedItemHandler={setSelectedUsersMutual} 
                                onResetSelectionsHandler={resetSelectedUsersMutual} />
                            <DateComponent
                                icon={filterIcon(Calendar)}
                                title='Date Connected'
                                dateFrom={connectedAtFrom}
                                dateTo={connectedAtTo}
                                dateFromValueChangedHandler={(e: any) =>  updateForm('connectedAtFrom', e)}
                                dateToValueChangedHandler={(e: any) =>  updateForm('connectedAtTo', e)}
                                resetFilter={() => resetDateFilter('connectedAtFrom', 'connectedAtTo')}/>
                            <LastMessagedFilterFragment
                                lastMessagedFrom={lastMessagedFrom}
                                lastMessagedTo={lastMessagedTo}
                                conversationStatus={conversationStatus}
                                selectedOwners={conversationOwners}
                                showOnlyLast={onlyLast}
                                filterOptions={workspaceUsers.map((user: IUser) => ({
                                    value: `${user.firstName} ${user.lastName}`,
                                    label: <Row alignItems gap="10px">
                                            <ProfilePhoto capitalLetters={`${user?.firstName?.charAt(0).toUpperCase()}${user?.lastName?.charAt(0).toUpperCase()}`} />
                                            <Column>
                                                <Text>{user?.firstName + ' ' + user?.lastName}</Text>
                                                <div style={{width: `150px`}}>
                                                    <Text $paragraph3 ref={textRef} $ellipsis="0">{user?.email}</Text>
                                                </div>
                                            </Column>
                                    </Row>,
                                    id: user._id,
                                    selected: false,
                                    object: user
                                }))}
                                upgradePlan={workspace?.isStarter}
                                inputTitle='Last Messaged'
                                isBusinessWs={workspace?.isBusiness} 
                                onSelectedOwnerHandler={updateFormLastMessaged}
                                onSelectedDateHandler={updateFormLastMessaged}
                                onStatusChangeHandler={updateFormLastMessaged}
                                onOnlyLastToggleHandler={updateFormLastMessaged}
                                resetFilter={() => resetLastMsgdFilter('lastMessagedFrom', 'lastMessagedTo', 'conversationStatus', 'conversationOwners', 'onlyLast')}>
                            </LastMessagedFilterFragment>
                            <FormControl $orientation="column">
                                <Select 
                                    prefixIcon={filterIcon(EyeOff)}
                                    fieldLabel="Hidden contacts"
                                    placeholder="Hidden contacts"
                                    isSearchable={false}
                                    options={hiddenConnectionsOptions}
                                    fieldActive={isSelectValueActive(connectionsFilter?.hiddenConnectionsValue, 'no')}
                                    value={connectionsFilter?.hiddenConnectionsValue ?? hiddenConnectionsOptions[0]}
                                    onChange={(e:any) => updateForm('hiddenConnectionsValue', e)}
                                />
                            </FormControl>
                        </FilterGrid>
                    )}
                </FilterSection>

                <FilterSection>
                    <SectionToggle type="button" onClick={() => toggleSection('contactInfo')}>
                        <ChevronDown size={14} style={{ transform: openSections.contactInfo ? undefined : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                        <SectionTitle>Contact info</SectionTitle>
                    </SectionToggle>
                    {openSections.contactInfo && (
                        <FilterGrid>
                            <DateComponent
                                icon={filterIcon(Cake)}
                                title='Date of Birth'
                                dateFrom={birthDateFrom}
                                dateTo={birthDateTo}
                                dateFromValueChangedHandler={(e: any) =>  updateForm('birthDateFrom', e)}
                                dateToValueChangedHandler={(e: any) =>  updateForm('birthDateTo', e)}
                                resetFilter={() => resetDateFilter('birthDateFrom', 'birthDateTo')}/>
                            <AdvancedFilterFragment
                                icon={filterIcon(Languages)}
                                filterOptions={languageFilterOptions.map((lang: string) => ({
                                    value: lang,
                                    label: lang,
                                    id: lang,
                                    selected: false,
                                    object: lang
                                }))} 
                                selectedItems={selectedLanguages.map( (language: string) => ({ _id: language }))}
                                infoTitle={infoTitleLanguages}
                                inputTitle={inputTitleLanguages}
                                tabs={[{title: 'And', slug: 'And', selected: languageCondition !== 'Or'}, {title: 'Or', slug: 'Or', selected: languageCondition === 'Or'}]} 
                                onSelectedTabHandler={(tab: string) => updateForm('languageCondition', tab)}
                                onSelectedItemHandler={setSelectedLanguages} 
                                onResetSelectionsHandler={resetSelectedLanguages} />
                            <FormControl $orientation="column">
                                <Select 
                                    prefixIcon={filterIcon(AtSign)}
                                    fieldLabel="Li Email"
                                    placeholder="Li Email"
                                    isSearchable={false}
                                    options={contactInfoOptions}
                                    fieldActive={isSelectValueActive(connectionsFilter?.contactInfo)}
                                    value={(connectionsFilter?.contactInfo && connectionsFilter?.contactInfo !== '' && connectionsFilter?.contactInfo !== 'all') ? connectionsFilter?.contactInfo : contactInfoOptions[0]}
                                    onChange={(e:any) => updateForm('contactInfo', e)}
                                />
                            </FormControl>
                            <FormControl $orientation="column">
                                <Select 
                                    prefixIcon={filterIcon(Phone)}
                                    fieldLabel="Li Phone"
                                    placeholder="Li Phone"
                                    isSearchable={false}
                                    options={liPhoneOptions}
                                    fieldActive={isSelectValueActive(connectionsFilter?.liPhone)}
                                    value={(connectionsFilter?.liPhone && connectionsFilter?.liPhone !== '' && connectionsFilter?.liPhone !== 'all') ? connectionsFilter?.liPhone : liPhoneOptions[0]}
                                    onChange={(e:any) => updateForm('liPhone', e)}
                                />
                            </FormControl>
                            <FormControl $orientation="column">
                                <Select 
                                    prefixIcon={filterIcon(Mail)}
                                    fieldLabel="Personal Email"
                                    placeholder="Personal Email"
                                    isSearchable={false}
                                    options={privateEmailOptions}
                                    fieldActive={isSelectValueActive(connectionsFilter?.privateEmail)}
                                    value={(connectionsFilter?.privateEmail && connectionsFilter?.privateEmail !== '' && connectionsFilter?.privateEmail !== 'all') ? connectionsFilter?.privateEmail : privateEmailOptions[0]}
                                    onChange={(e:any) => updateForm('privateEmail', e)}
                                />
                            </FormControl>  
                            <FormControl $orientation="column">
                                <Select 
                                    prefixIcon={filterIcon(Mail)}
                                    fieldLabel="Business Email"
                                    placeholder="Business Email"
                                    isSearchable={false}
                                    options={businessEmailOptions}
                                    fieldActive={isSelectValueActive(connectionsFilter?.businessEmail)}
                                    value={(connectionsFilter?.businessEmail && connectionsFilter?.businessEmail !== '' && connectionsFilter?.businessEmail !== 'all') ? connectionsFilter?.businessEmail : businessEmailOptions[0]}
                                    onChange={(e:any) => updateForm('businessEmail', e)}
                                />
                            </FormControl>
                            <FormControl $orientation="column">
                                <Select 
                                    prefixIcon={filterIcon(Phone)}
                                    fieldLabel="Phone"
                                    placeholder="Phone"
                                    isSearchable={false}
                                    options={phoneOptions}
                                    fieldActive={isSelectValueActive(connectionsFilter?.phone)}
                                    value={(connectionsFilter?.phone && connectionsFilter?.phone !== '' && connectionsFilter?.phone !== 'all') ? connectionsFilter?.phone : phoneOptions[0]}
                                    onChange={(e:any) => updateForm('phone', e)}
                                />
                            </FormControl>
                            <MinMaxRangeDropdown
                                icon={filterIcon(UserPlus)}
                                prefilledMinValue={searchByFollowers}
                                prefilledMaxValue={searchByMaxFollowers}
                                title={inputTitleFollowers}
                                onChangeHandler={({ minValue, maxValue }: { minValue?: number, maxValue?: number }) => updateMinMax([ ...[ minValue ? { 'searchByFollowers': minValue } : {}], ...[ maxValue ? { 'searchByMaxFollowers': maxValue } : {}] ])}
                                description={<span>Enter the minimum and maximum number <br /> of followers</span>}
                                resetFilter={()=>resetDateFilter('searchByFollowers', 'searchByMaxFollowers')}
                            />
                        </FilterGrid>
                    )}
                </FilterSection>

                <FilterSection>
                    <SectionToggle type="button" onClick={() => toggleSection('work')}>
                        <ChevronDown size={14} style={{ transform: openSections.work ? undefined : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                        <SectionTitle>Work</SectionTitle>
                    </SectionToggle>
                    {openSections.work && (
                        <FilterGrid>
                            <FullWidth>
                                <JobCompanyFilterFragment
                                    icon={filterIcon(RefreshCw)}
                                    hasJobChanges={hasJobChanges === 'yes'}
                                    hasCompanyChanges={hasCompanyChanges === 'yes'}
                                    infoTitle="Filter by job and company changes"
                                    inputTitle="Job/Company Changes"
                                    onJobChangesHandler={(value) => setHasJobChanges(value)}
                                    onCompanyChangesHandler={(value) => setHasCompanyChanges(value)}
                                    onResetHandler={() => {
                                        setHasJobChanges(false);
                                        setHasCompanyChanges(false);
                                    }}
                                />
                            </FullWidth>
                            <AdvancedSearchDropdown
                                icon={filterIcon(Briefcase)}
                                label="Industry"
                                placeholder="Search industry"
                                options={industryOptions}
                                value={(connectionsFilter?.industry && connectionsFilter?.industry !== '') ? { label: connectionsFilter?.industry.label?.replace?.(/^Industry:\s*/, '') || connectionsFilter?.industry.label, value: connectionsFilter?.industry.value } : { label: "All Industries", value: null }}
                                onClick={(e:any) => updateForm('industry', e)}
                            />
                            <MinMaxRangeDropdown
                                icon={filterIcon(Building2)}
                                prefilledMinValue={companyEmployeesMin}
                                prefilledMaxValue={companyEmployeesMax}
                                title={inputTitleCompanyEmployees}
                                onChangeHandler={({ minValue, maxValue }: { minValue?: number, maxValue?: number }) =>
                                    updateMinMax([
                                        ...(minValue !== undefined ? [{ companyEmployeesMin: minValue }] : [{}]),
                                        ...(maxValue !== undefined ? [{ companyEmployeesMax: maxValue }] : [{}]),
                                    ])
                                }
                                description={<span>Enter the minimum and maximum number <br /> of employees</span>}
                                resetFilter={() => resetDateFilter('companyEmployeesMin', 'companyEmployeesMax')}
                            />
                        </FilterGrid>
                    )}
                </FilterSection>

                <FilterSection>
                    <SectionToggle type="button" onClick={() => toggleSection('tagsNotes')}>
                        <ChevronDown size={14} style={{ transform: openSections.tagsNotes ? undefined : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                        <SectionTitle>Tags & notes</SectionTitle>
                    </SectionToggle>
                    {openSections.tagsNotes && (
                        <FilterGrid>
                            <AdvancedFilterFragment
                                icon={filterIcon(Tag)}
                                drop='up'
                                filterOptions={[ {
                                        value: '',
                                        label: 'Without Tags',
                                        id: 'noTags',
                                        selected: false,
                                        object: {
                                            _id: 'noTags'
                                        }
                                    }, 
                                    ...tagFilterOptions?.filter((tag: ITag) => !tag.isForbidden).map((tag: ITag) => ({
                                        value: tag.title,
                                        label: <AppTag
                                               tag={tag}
                                            />,
                                        id: tag._id,
                                        selected: false,
                                        object: tag
                                }))]} 
                                selectedItems={[...selectedTags, ...(connectionsFilter.noTags ? [{ _id: 'noTags' }] : []) ]}
                                infoTitle={infoTitleTags}
                                inputTitle={inputTitleTags}
                                tabs={[{title: 'And', slug: 'And', selected: tagCondition !== 'Or'}, {title: 'Or', slug: 'Or', selected: tagCondition === 'Or'}]}
                                onSelectedTabHandler={(tab: string) => updateForm('tagCondition', tab)}
                                onSelectedItemHandler={setSelectedTags} 
                                onResetSelectionsHandler={resetSelectedTags}
                            />
                            <AdvancedFilterFragment
                                icon={filterIcon(Tags)}
                                drop='up'
                                filterOptions={excludeTagFilterOptions?.filter((tag: ITag) => !tag.isForbidden).map((tag: ITag) => ({
                                        value: tag.title,
                                        label: <AppTag
                                                tag={tag}
                                            />,
                                        id: tag._id,
                                        selected: false,
                                        object: tag
                                }))} 
                                selectedItems={selectedExcludeTags}
                                infoTitle={infoTitleTags}
                                inputTitle={inputTitleExcludeTags}
                                tabs={[{title: 'And', slug: 'And', selected: tagCondition !== 'Or'}, {title: 'Or', slug: 'Or', selected: tagCondition === 'Or'}]} 
                                onSelectedTabHandler={(tab: string) => updateForm('tagCondition', tab)}
                                onSelectedItemHandler={setSelectedExcludeTags} 
                                onRemovedItemHandler={removeSelectedExcludeTags}
                                onResetSelectionsHandler={resetSelectedExcludeTags} />
                            <AutotagsFilterFragment
                                icon={filterIcon(Sparkles)}
                                autotagsFilter={autotagsFilter}
                                infoTitle={infoTitleTags}
                                inputTitle={inputTitleAutotags}
                                onSelectedTabHandler={(tab: string) =>
                                updateFormAutotags("isAutotagsFilterOr", tab)
                                }
                                onSelectedAutotagsHandler={updateFormAutotags}
                                onResetAutotagsHandler={resetSelectedAutotags}
                            /> 
                            <FormControl $orientation="column">
                                <Select 
                                    prefixIcon={filterIcon(StickyNote)}
                                    fieldLabel="Note"
                                    placeholder="Notes"
                                    isSearchable={false}
                                    options={notesOptions}
                                    fieldActive={isSelectValueActive(connectionsFilter?.note)}
                                    value={(connectionsFilter?.note && connectionsFilter?.note !== '' && connectionsFilter?.note !== 'all') ? connectionsFilter?.note : notesOptions[2]}
                                    onChange={(e:any) => updateForm('note', e)}
                                />
                            </FormControl>
                        </FilterGrid>
                    )}
                </FilterSection>
            </FiltersBody>
            <FooterActions>
                <ClearButton type="button" onClick={() => resetFormHandler()}>Clear filters</ClearButton>
                <FooterRight>
                    <SaveButton type="button" onClick={(e: any) => saveSavedFiltersHandler(e)}>Save filters</SaveButton>
                    <ResultsButton type="button" onClick={(e: any) => showResults(e)}>Results</ResultsButton>
                </FooterRight>
            </FooterActions>
        </StyledForm>
    )
}

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    gap: 0;
`
const FilterHeader = styled.div`
    flex-shrink: 0;
    padding: 20px 24px 16px;
`
const FilterTitle = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: ${styles.colors.black600};
`
const FiltersBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 16px 24px;
`
const FilterSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 10px;
`
const SectionToggle = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    color: ${styles.colors.black300};

    &:hover {
        color: ${styles.colors.black400};
    }
`
const SectionTitle = styled.h3`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: inherit;
`
const FilterGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    & > * {
        min-width: 0;
        width: 100%;
    }

    & .dropdown,
    & .dropup {
        width: 100%;
        min-width: 0;
        /* the toggle owns the field surface - a wrapper background shows through its rounded corners */
        background-color: transparent !important;
        border-radius: 8px;
    }

    & .actions-dropdown + div {
        max-height: min(600px, calc(100vh - 120px));
    }

    /* search fields inside the filter panels - number inputs sit inside their own
       bordered stepper (InputNumber) and must keep their borderless look */
    & .actions-dropdown + div input:not([type='number']):not([type='checkbox']):not([type='radio']):not([type='hidden']) {
        height: 34px;
        border: 1px solid ${styles.colors.black200} !important;
        border-radius: 8px !important;
        font-weight: 400;

        &:hover { border-color: ${styles.colors.black300} !important; }
        &:focus { border-color: ${styles.colors.primary600} !important; }
    }

    & .dropdown-toggle,
    & .dropdownWrapperReference {
        width: 100%;
        min-width: 0;
        overflow: hidden;
    }

    & .dropdown-toggle {
        height: 40px !important;
        min-height: 40px !important;
        padding: 0 12px !important;
        border-radius: 8px !important;
        outline: none !important;
        box-shadow: none !important;
        /* a button's default text-align is center, which pushes field values away from their label */
        text-align: left !important;
        border: 1px solid ${styles.colors.black200} !important;
        background-color: ${({ theme: { secondaryColor } }) => secondaryColor} !important;
        box-sizing: border-box;
        color: ${styles.colors.black600} !important;
        font-weight: 500 !important;
        font-size: 14px !important;

        &:hover,
        &:focus {
            outline: none !important;
            border-color: ${styles.colors.black300} !important;
            background-color: ${styles.colors.black100} !important;
            color: ${styles.colors.black600} !important;
        }
    }

    & .dropdownWrapperReference {
        gap: 8px;
        height: 100%;
        /* the dropdown base makes its trigger look like a link (bold, primary) */
        font-weight: 400;
        color: ${styles.colors.black600};
    }

    & .dropdownWrapperReference > svg:last-child {
        flex-shrink: 0;
        margin-left: auto;
        width: 14px;
        height: 14px;
    }

    & .dropdownWrapperReference .svg,
    & .dropdownWrapperReference svg.svg {
        width: 16px !important;
        height: 16px !important;
        max-width: 16px;
        max-height: 16px;
        flex-shrink: 0;
    }

    & .dropdownWrapperReference .svg path,
    & .dropdownWrapperReference svg.svg path {
        fill: ${styles.colors.black300};
    }

    & .filter-field-icon {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        color: ${styles.colors.black300};
    }

    & .filter-field-icon path {
        fill: none !important;
        stroke: currentColor;
    }

    & .dropdownWrapperReference > div {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    & [class*='-control'] {
        height: 40px !important;
        min-height: 40px !important;
        border-radius: 8px !important;
        border: 1px solid ${styles.colors.black200} !important;
        box-shadow: none !important;
        padding: 0 8px !important;
        gap: 8px;
        background-color: ${({ theme: { secondaryColor } }) => secondaryColor} !important;

        &:hover {
            border-color: ${styles.colors.black300} !important;
            background-color: ${styles.colors.black100} !important;
        }
    }

    & [class*='-ValueContainer'] {
        min-width: 0;
        overflow: hidden;
        padding: 0 !important;
    }

    & [class*='-singleValue'] {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin: 0 !important;
        position: static !important;
        transform: none !important;
    }

    & [class*='-IndicatorsContainer'] {
        flex-shrink: 0;
    }

    & [class*='-indicatorContainer'] {
        padding: 0 !important;
        color: ${styles.colors.black300} !important;
    }

    & [class*='-SelectContainer'],
    & [class*='-container'] {
        width: 100%;
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`
const FullWidth = styled.div`
    grid-column: 1 / -1;
    min-width: 0;
`
const FooterActions = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 24px;
    border-top: 1px solid ${styles.colors.black200};
    background: ${({ theme: { secondaryColor } }) => secondaryColor};
`
const FooterRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`
const ClearButton = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: ${styles.colors.red600};

    &:hover {
        text-decoration: underline;
    }
`
const SaveButton = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: ${styles.colors.primary600};

    &:hover {
        text-decoration: underline;
    }
`
const ResultsButton = styled.button`
    height: 36px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    background: ${styles.colors.primary600};
    color: ${styles.colors.white};
    transition: background-color 0.15s ease;

    &:hover {
        background: ${styles.colors.primary500};
    }
`
const StyledContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
`
/* keeps long names/emails inside the panel instead of making it scroll sideways */
const MemberInfo = styled(Column)`
    min-width: 0;
    overflow: hidden;
`
export default FilterConnectionsForm